import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { HOME_PATH } from '@/shared/config';
import { BrandLogo } from '@/shared/ui';

interface AuthBrandPanelProps {
  /** HOME 히어로 배너 첫 장 — 없거나 불러오지 못하면 흑연 판만 깐다. */
  readonly imageUrl: string | null;
}

/**
 * 인증 화면 브랜드 판 — 흑연 바탕에 흰 로고와 라임 선, 회사 소개와 같은 한 줄 선언.
 * 넓은 화면에서는 왼쪽 절반을 세로로 채우고(배너 사진을 어둡게 깐다), 좁은 화면에서는 로고만 담은 띠로 줄어든다.
 */
export const AuthBrandPanel = ({ imageUrl }: AuthBrandPanelProps): ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = imageUrl !== null && failedUrl !== imageUrl;

  return (
    <div className="relative shrink-0 overflow-hidden bg-primary text-white lg:sticky lg:top-0 lg:h-dvh lg:w-[46%]">
      {showImage ? (
        <>
          <img
            src={imageUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 hidden size-full object-cover lg:block"
            onError={() => setFailedUrl(imageUrl)}
          />
          <div
            aria-hidden
            className="absolute inset-0 hidden bg-gradient-to-t from-black/95 via-black/50 to-black/30 lg:block"
          />
        </>
      ) : null}

      <div className="relative flex h-full flex-col justify-between gap-5 px-5 py-6 sm:px-8 lg:px-14 lg:py-12">
        <div className="flex items-center justify-between gap-4">
          <Link
            to={HOME_PATH}
            aria-label="SuperStructure 홈으로"
            className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime"
          >
            <BrandLogo className="h-7 sm:h-8 lg:h-10" />
          </Link>
          <span aria-hidden className="block h-px w-10 bg-brand-lime lg:hidden" />
        </div>

        <div className="hidden flex-col gap-6 lg:flex">
          <span aria-hidden className="block h-px w-14 bg-brand-lime" />
          <p className="text-[clamp(1.875rem,2.6vw,2.75rem)] leading-[1.28] font-semibold tracking-[-0.03em] text-balance">
            브랜드를 담은 굿즈,
            <br />
            디자인부터 배송까지 한곳에서.
          </p>
          <p className="max-w-sm text-[15px] leading-relaxed text-white/70">
            상품 사진 위에서 직접 디자인하고, 상담으로 견적을 확정한 뒤 주문마다 제작해 보내
            드립니다.
          </p>
          <p className="mt-6 text-[13px] text-white/45">© 2026 주식회사 슈퍼스트럭처</p>
        </div>
      </div>
    </div>
  );
};
